import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { Box, Grid, type GridProps } from '@mui/material'
import { styled } from '@mui/material/styles'
import {
  LanguageUtils,
  type PrimitiveTableRowProps,
  Text,
} from '@pbt/pbt-ui-components'
import { getColumnStyles } from '@pbt/pbt-ui-components/src/utils/primitiveTableUtils'

import type {
  InventoryOrder,
  InventoryOrderItem,
} from '~/api/graphql/generated/types'
import { InventoryOrderItemStatus } from '~/constants/inventoryOrders'
import { getPackageType, getProductSizeUnit } from '~/store/reducers/constants'

import { calculatePackageCount } from '../../inventoryUtils'
import InventoryOrderItemStatusLabel from '../InventoryOrderItemStatusLabel'
import {
  isReceivingDisabled,
  transformInventoryOrderItemForTable,
} from '../utils'
import { getInventoryItemDisplayQuantity } from '../utils/inventoryItemUtils'
import { OrderItemFlags } from './OrderItemFlags'

import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'

interface StyledRowContainerProps extends GridProps {
  index: number
}

const StyledRowContainer = styled(Grid)<StyledRowContainerProps>`
  border-top: none;
  border-bottom: none;
  background-color: ${({ index, theme }) =>
    index % 2 === 0 ? theme.colors.grayGray4 : 'unset'};
`
const StyledQuantityContainer = styled('div')`
  display: flex;
  flex-direction: column;
`
const StyledQuantityTotal = styled(Text)`
  font-weight: bold;
  font-size: ${({ theme }) => theme.typography.body1.fontSize};
  line-height: 1.8rem;
`
const StyledPackageType = styled('span')`
  color: ${({ theme }) => theme.colors.grayGray2};
  font-size: ${({ theme }) => theme.typography.body1.fontSize};
`

interface OrderItemTableRowProps extends PrimitiveTableRowProps {
  index: number
  inventoryOrder: InventoryOrder
  item: InventoryOrderItem
}

export const OrderItemTableRow = ({
  item,
  inventoryOrder,
  index,
  data: { mainColumnWidth, otherColumns },
}: OrderItemTableRowProps) => {
  const { t } = useTranslation(['Admin', 'Common'])
  const PackageType = useSelector(getPackageType)
  const ProductSizeUnit = useSelector(getProductSizeUnit)
  const navigate = useNavigate()

  const {
    id,
    name,
    statusId,
    orderedQuantity,
    receivedQuantity,
    rejectedQuantity,
    packageTypeId,
    controlled,
    storeCold,
    sku,
  } = transformInventoryOrderItemForTable(item)

  const packageTypeName = LanguageUtils.getConstantTranslatedName(
    packageTypeId,
    PackageType,
  )
  const productSizeUnitName = LanguageUtils.getConstantTranslatedName(
    item.packageUnits.id,
    ProductSizeUnit,
  )
  const { perPackageAmount } = item

  const formatQuantityDisplay = (quantity: number): ReactNode => {
    const { total, breakdown } = getInventoryItemDisplayQuantity({
      orderedQuantity: quantity,
      perPackageAmount,
      productSizeUnit: productSizeUnitName,
      packageType: packageTypeName,
      breakdownStyle: 'simplified',
    })

    return (
      <StyledQuantityContainer>
        <StyledQuantityTotal>{total}</StyledQuantityTotal>
        {breakdown && <StyledPackageType>({breakdown})</StyledPackageType>}
      </StyledQuantityContainer>
    )
  }

  const formatTotalPackages = (quantity: number): ReactNode => {
    const totalPackages = calculatePackageCount(quantity, perPackageAmount)
    const { total, breakdown } = getInventoryItemDisplayQuantity({
      orderedQuantity: totalPackages,
      perPackageAmount,
      productSizeUnit: productSizeUnitName,
      packageType: packageTypeName,
      originalQuantity: quantity,
      breakdownStyle: 'simplified',
    })

    return (
      <StyledQuantityContainer>
        <StyledQuantityTotal>{total}</StyledQuantityTotal>
        {breakdown && <StyledPackageType>({breakdown})</StyledPackageType>}
      </StyledQuantityContainer>
    )
  }

  const navigateToItem = () => {
    navigate(`items/${id}`)
  }

  const buttonText = isReceivingDisabled(inventoryOrder.status, item.status)
    ? t('Common:VIEW_ACTION')
    : t('Admin:CATALOG.ORDERS.RECEIVE_ACTION')

  const getButtonEmphasis = (): 'primary' | 'tertiary' => {
    if (isReceivingDisabled(inventoryOrder.status, item.status)) {
      return 'tertiary'
    }

    const statusName = item.status.name

    if (
      statusName === InventoryOrderItemStatus.ORDERED ||
      statusName === InventoryOrderItemStatus.PARTIAL
    ) {
      return 'primary'
    }

    return 'tertiary'
  }

  return (
    <StyledRowContainer container alignItems="center" index={index} p={1}>
      <Grid
        item
        alignItems="center"
        display="flex"
        gap={1}
        justifyContent="space-between"
        px={0.5}
        style={getColumnStyles(mainColumnWidth)}
      >
        <Box display="flex" flexDirection="column" gap={1} minWidth={0}>
          <Text variant="body2">{name}</Text>
          <OrderItemFlags controlled={controlled} storeCold={storeCold} />
        </Box>
        <KibButtonNew emphasis={getButtonEmphasis()} onClick={navigateToItem}>
          {buttonText}
        </KibButtonNew>
      </Grid>
      <Grid
        container
        item
        alignItems="center"
        style={getColumnStyles(12 - mainColumnWidth)}
      >
        <Grid
          item
          display="flex"
          px={0.5}
          style={getColumnStyles(otherColumns[0].width)}
        >
          <InventoryOrderItemStatusLabel statusId={statusId} />
        </Grid>
        <Grid item px={0.5} style={getColumnStyles(otherColumns[1].width)}>
          {formatQuantityDisplay(orderedQuantity)}
        </Grid>
        <Grid item px={0.5} style={getColumnStyles(otherColumns[2].width)}>
          {receivedQuantity != null
            ? formatTotalPackages(receivedQuantity)
            : '--'}
        </Grid>
        <Grid item px={0.5} style={getColumnStyles(otherColumns[3].width)}>
          {rejectedQuantity != null && rejectedQuantity > 0
            ? formatTotalPackages(rejectedQuantity)
            : '--'}
        </Grid>
        <Grid item px={0.5} style={getColumnStyles(otherColumns[4].width)}>
          <Text variant="body2">{sku ?? '-'}</Text>
        </Grid>
      </Grid>
    </StyledRowContainer>
  )
}
